import qapi from "views_shared/app/javascript/api";
import { ApiMutationError } from "views_shared/app/javascript/api/api";

// The contact-info mutation clients are shared with the company edit forms, which read the
// `{ error }` result directly, so they cannot be made to throw. Convert here instead: every one of
// add/update/remove has to be able to fail loudly, or a rejected value (the server validates each
// type's format) would look like a save and vanish on the next reload.
const settle = async (call) => {
  const result = await call();
  if (result?.error) throw new ApiMutationError(result.error);
  return result;
};

// Context-fed action hook for an owner's contact infos — the collection behind both a Contact card
// and its editor. Generic over the polymorphic owner (media outlet, media profile, ...); the caller
// supplies which one this is. The provider owns the collection, so every mutation reconciles by
// reloading rather than patching local state: contact rows are few, and the reload is what assigns
// a new row its server id (which the editor keys on).
const useContactInfosApi = ({
  contactOwnerId,
  contactOwnerType,
  silentReload,
  setGeneralError,
  setIsUpdating,
  isUpdating,
}) => {
  const runMutation = async (call) => {
    if (isUpdating) return; // guard overlapping mutations
    setGeneralError(null);
    setIsUpdating(true);
    try {
      await settle(call);
      await silentReload();
    } catch (error) {
      setGeneralError(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const addContactInfo = async ({ type, value }) =>
    runMutation(() =>
      qapi.contactInfos.createByContactOwner(contactOwnerId, contactOwnerType, { value, value_type: type }),
    );

  const updateContactInfo = async (id, { type, value }) =>
    runMutation(() => qapi.contactInfos.updateById(id, { value, value_type: type }));

  const removeContactInfo = async (id) => runMutation(() => qapi.contactInfos.deleteById(id));

  return { addContactInfo, removeContactInfo, updateContactInfo };
};

export default useContactInfosApi;
